import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUrl, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { FormSubmissionData } from '@/types/api';
import { isAdminSurface } from './surface';

/**
 * The submissions inbox. One component serves both surfaces; the portal hits
 * its own routes, which scope by permission and omit the raw payload entirely.
 */

export type SubmissionFilters = {
  client_id?: number;
  client_form_id?: number;
  site_id?: number;
  status?: string;
  flagged?: boolean;
  q?: string;
  page?: number;
  /** Show the Deleted view instead of the inbox. A mode, not a narrowing. */
  deleted?: boolean;
};

const base = () => (isAdminSurface ? '/v1/form-submissions' : '/v1/portal/form-submissions');
const LIST_KEY = ['form-submissions'] as const;

export function useSubmissions(filters: SubmissionFilters = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, filters],
    queryFn: async (): Promise<Paginated<FormSubmissionData>> => {
      const { data } = await api.get<Paginated<FormSubmissionData>>(base(), { params: filters });
      return data;
    },
  });
}

export function useSubmission(id: number | undefined) {
  return useQuery({
    queryKey: id ? [...LIST_KEY, id] : [...LIST_KEY, 'none'],
    enabled: id != null,
    queryFn: async (): Promise<FormSubmissionData> => {
      const { data } = await api.get<FormSubmissionData>(`${base()}/${id}`);
      return data;
    },
  });
}

export function useUpdateSubmissionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }): Promise<FormSubmissionData> => {
      await primeCsrf();
      const { data } = await api.patch<FormSubmissionData>(`${base()}/${id}`, { status });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useDeleteSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`${base()}/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

/**
 * Soft-deleted submissions keep their files, which is the only reason a restore
 * is worth offering.
 */
export function useRestoreSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.post(`${base()}/${id}/restore`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

/**
 * Empties the Deleted view for good, rows and attachments alike. Takes the same
 * filters as the list so it never reaches past what is on screen.
 */
export function usePurgeSubmissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (filters: SubmissionFilters = {}): Promise<number> => {
      await primeCsrf();
      const { data } = await api.delete<{ purged: number }>(`${base()}/trashed`, {
        params: { ...filters, page: undefined },
      });
      return data.purged;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

/**
 * CSV export URL. A plain link rather than an axios call so the browser handles
 * the download; filters ride along so the file matches what is on screen.
 */
export function exportUrl(filters: SubmissionFilters = {}): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== false) params.set(k, String(v));
  });
  const qs = params.toString();
  return apiUrl(`${base()}.csv${qs ? `?${qs}` : ''}`);
}
